import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { LeftMenuItem } from '../models/left-menu-item.model';
import { TitleService } from '../../utils/TitleService';
import { Subscription } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService, UserDetailsResponse } from '../../auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthContext } from '../../auth/services/auth.context';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LookUpService } from '../../core/services/lookup.service';

@Component({
  selector: 'left-navigation-layout',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatTooltipModule
  ],
  templateUrl: './navigation-layout.component.html',
  styleUrl: './navigation-layout.component.scss'
})
export class NavigationLayoutComponent implements OnInit {

  private snackBar = inject(MatSnackBar);

  userMenu: LeftMenuItem[] = [];
  title: string = '';
  userDetails: UserDetailsResponse | null = null; // Ensure this can be null
  expandLeftNav: boolean = false;
  routeSubscription: Subscription;

  userGroups = [
    { groupCode: 'ROLE_USER', groupName: 'User Role' },
    { groupCode: 'ROLE_JE', groupName: 'Junior Engineer' },
    { groupCode: 'ROLE_AGE', groupName: 'Asst Garrison Engineer' },
    { groupCode: 'ROLE_ADMIN', groupName: 'Admin Role' },
    { groupCode: 'ROLE_GE', groupName: 'Garrison Engineer' },
    { groupCode: 'ROLE_CE', groupName: 'Chief Engineer / Commander Works' },
    { groupCode: 'ROLE_ZS', groupName: 'Zone/Area Supervisor' },
  ];

  constructor(
    private titleService: TitleService,
    private router: Router,
    private authService: AuthService,
    private authContext: AuthContext,
    private cdr: ChangeDetectorRef,
    private lookUpService: LookUpService
  ) {
    this.title = this.titleService.getTitle();
    this.routeSubscription = this.titleService.onTitleChange.subscribe(title => {
      this.title = title;
      console.log(title);
    });
  }

  ngOnInit(): void {
    this.getLookUps();
    const userInfo = this.authContext.getUserInfo();
    if (userInfo) {
      this.userDetails = userInfo;
      this.buildUserMenu(userInfo.userType); // Safely pass userType
    } else {
      this.buildUserMenu('ROLE_USER'); // Safely pass userType
    }
  }

  ngAfterViewInit() {
    this.cdr.detectChanges(); // Manually trigger change detection
  }

  getUserGroup(groupCode: string | undefined) {
    const code = this.userGroups.find(item => item.groupCode === groupCode)?.groupName;
    if (groupCode === 'ROLE_USER') {
      return '';
    }
    return code ? code : '';
  }

  menuConfig = [
    {
      name: 'Home',
      icon: 'bi bi-house',
      route: '/app/home',
      roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE', 'ROLE_CE', 'ROLE_USER'],
      subMenuItems: []
    },
    {
      name: 'Reports',
      icon: 'bi bi-journal-text',
      route: '/app/reports',
      roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE', 'ROLE_CE'],
      subMenuItems: [
        { name: 'Complaint Summary', route: '/app/reports/complaints-summary-report', roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE'] },
        { name: 'Complaint Reports', route: '/app/reports/complaint-report', roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE', 'ROLE_CE'] },
        { name: 'Job Card Reports', route: '/app/reports/job-card-report', roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE'] }
      ]
    },
    {
      name: 'Manage Complaints',
      icon: 'bi bi-book',
      route: '/app/manage-complaints',
      roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE', 'ROLE_CE'],
      subMenuItems: [
        { name: 'Manage Job Cards', route: '/app/manage-complaints/manage-job-cards', roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE'] },
        { name: 'Create Complaint', route: '/app/create-complaint', roles: ['ROLE_ADMIN', 'ROLE_JE', 'ROLE_AGE', 'ROLE_GE', 'ROLE_CE'] }
      ]
    },
    {
      name: 'User Management',
      icon: 'bi bi-people',
      route: '/app/user-management',
      roles: ['ROLE_ADMIN'],
      subMenuItems: [
        { name: 'Manage User Requests', route: '/app/user-management/user-requests', roles: ['ROLE_ADMIN'] },
        { name: 'Manage Residents', route: '/app/user-management/manage-residents', roles: ['ROLE_ADMIN'] },
      ]
    },
        {
          name: 'Manage Employees',
          icon: 'bi bi-person',
          active: true,
          route: '/app/manage-employees',
          roles: ['ROLE_ADMIN'],
          defaultExpanded: false,
          subMenuItems: []
        },
  ];

  buildUserMenu(userType: string) {
    this.userMenu = this.menuConfig
      .filter(item => item.roles.includes(userType))
      .map(item => {
        const subMenuItems = item.subMenuItems
          .filter(sub => sub.roles.includes(userType))
          .map(sub => ({
            name: sub.name,
            route: sub.route,
            active: true
          }));

        // Handle specific route overrides for ROLE_CE to maintain original behavior
        let route = item.route;
        if (userType === 'ROLE_CE') {
          if (item.name === 'Reports') route = '/app/reports/complaint-report';
          if (item.name === 'Manage Complaints') route = '/app/create-complaint';
        }

        return {
          name: item.name,
          icon: item.icon,
          active: true,
          route: route,
          defaultExpanded: false,
          subMenuItems: subMenuItems
        };
      });
  }

  userProfile() {
    this.router.navigate(['/app/user-profile'], { replaceUrl: true });
  }

  changePassword() {
    this.router.navigate(['/app/user-profile/change-password'], { replaceUrl: true });
  }

  createNewComplaint() {
    this.router.navigate(['/app/create-my-complaint'], { replaceUrl: true });
  }

  openSnackBar(message: string, action?: string) {
    this.snackBar.open(message, action, { duration: 3 * 1000 }); // 3 seconds
  }

  onLeftNavToggle() {
    this.expandLeftNav = !this.expandLeftNav;
  }

  onExpandMenuItem(index: number) {
    this.userMenu[index].defaultExpanded = !this.userMenu[index].defaultExpanded;
  }

  getLookUps() {
    this.lookUpService.getLookUps().subscribe({
      next: (response) => {
        if (response) {
          this.authContext.setLookups(response);
        }
      },
      error: (error) => {
        console.error('POST error:', error);
      },
    });
  }

  userLogout() {
    this.authService.logoutUser().subscribe({
      next: (response) => {
        // console.log('POST response:', response)
        if (response) {
          // console.log(response.message);
          this.openSnackBar(response.message, "Okay");
          localStorage.clear();
          this.router.navigate(['/app/login'], { replaceUrl: true });
        }
      },
      error: (error) => {
        console.error('POST error:', error);
        this.openSnackBar("User Logged Out Successfully", "Okay");
        localStorage.clear();
        this.router.navigate(['/app/login'], { replaceUrl: true });
      },
    });
  }

}
